import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MenuLinkTypeEnum } from 'common/components/entities/Menu/enums/MenuLinkTypeEnum'
import { MenuTargetEnum } from 'common/components/entities/Menu/enums/MenuTargetEnum'
import { BaseMenuItemInterface } from 'common/components/entities/Menu/types/MenuInterface'
import BlogLink from 'client/components/core/Sidebar/components/Settings/components/BlogLink'
import MenuItemTitle from 'client/components/core/Sidebar/components/Settings/entities/MenuSettings/components/MenuItem/MenuItemTitle'
import MenuItemDetailsUi from 'client/components/core/Sidebar/components/Settings/entities/MenuSettings/components/MenuItem/ui/MenuItemDetailsUi'
import MenuItemInputUi from 'client/components/core/Sidebar/components/Settings/entities/MenuSettings/components/MenuItem/ui/MenuItemInputUi'
import { menuLinkTypeLocoKeys } from 'client/components/core/Sidebar/components/Settings/entities/MenuSettings/constants/MenuLinkTypeLocoKeys'
import { detectLinkType } from 'client/components/core/Sidebar/components/Settings/entities/MenuSettings/utils/detectLinkType'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { WebsiteLink } from 'client/components/core/Sidebar/components/Settings/components/website-link'
import { isBlogReplacementPlaceholderValue } from 'common/utils/blogReplacementsUtils'
import { isWebsitePageUrlPlaceholderValue } from 'common/utils/website-replacements-utils'

interface BaseMenuItemProps {
  item: BaseMenuItemInterface
  number: number
  updateText: (e: React.ChangeEvent<HTMLInputElement>) => void
  updateLink: (link: string) => void
  updateTarget: (checked: boolean) => void
  remove: () => void
  isAllowedBlogLinks: boolean
  isAllowedWebsiteLinks: boolean
}

const BaseMenuItem = ({
  item,
  number,
  updateText,
  updateLink,
  remove,
  isAllowedBlogLinks,
  isAllowedWebsiteLinks,
  updateTarget,
}: BaseMenuItemProps) => {
  const { t } = useTranslation()
  const { text, link, target } = item

  const [selectedLinkType, setSelectedLinkType] = useState(
    detectLinkType(item, isAllowedBlogLinks, isAllowedWebsiteLinks),
  )

  const onChangeOption = (option: OptionSingleValueType<MenuLinkTypeEnum>) => {
    if (option) {
      if (
        (isBlogReplacementPlaceholderValue(item.link) ||
          isWebsitePageUrlPlaceholderValue(item.link)) &&
        option.value === MenuLinkTypeEnum.link_type_custom_url
      ) {
        updateLink('')
      }
      setSelectedLinkType(option.value)
    }
  }

  const options = useMemo(() => {
    const resOptions = [
      {
        value: MenuLinkTypeEnum.link_type_custom_url,
        label: t(menuLinkTypeLocoKeys[MenuLinkTypeEnum.link_type_custom_url]),
      },
    ]
    if (isAllowedBlogLinks) {
      resOptions.push({
        value: MenuLinkTypeEnum.link_type_blog_page,
        label: t(menuLinkTypeLocoKeys[MenuLinkTypeEnum.link_type_blog_page]),
      })
    } else if (isAllowedWebsiteLinks) {
      resOptions.push({
        value: MenuLinkTypeEnum.link_type_website_page,
        label: t(menuLinkTypeLocoKeys[MenuLinkTypeEnum.link_type_website_page]),
      })
    }
    return resOptions
  }, [t, isAllowedBlogLinks, isAllowedWebsiteLinks])

  return (
    <MenuItemDetailsUi>
      <MenuItemTitle
        number={number}
        text={text}
        updateText={updateText}
        remove={remove}
      />
      {(isAllowedBlogLinks || isAllowedWebsiteLinks) && (
        <SelectNew
          withoutWrapper
          onChange={onChangeOption}
          options={options}
          selectedOption={{
            value: selectedLinkType,
            label: t(menuLinkTypeLocoKeys[selectedLinkType]),
          }}
        />
      )}
      {selectedLinkType === MenuLinkTypeEnum.link_type_blog_page ? (
        <BlogLink blogLink={link} update={updateLink} />
      ) : selectedLinkType === MenuLinkTypeEnum.link_type_website_page ? (
        <WebsiteLink link={link} update={updateLink} />
      ) : (
        <MenuItemInputUi
          value={link}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            updateLink(e.target.value)
          }
          placeholder={t('entity_settings.menu.item.link_placeholder')}
        />
      )}
      <CheckboxNew
        label={'entity_settings.menu.item.target_label'}
        onChange={updateTarget}
        checked={target === MenuTargetEnum._blank}
      />
    </MenuItemDetailsUi>
  )
}

export default BaseMenuItem
